import {
  documentMimeTypes,
  imageMimeTypes,
  mimeTypes,
  videoMimeTypes,
  PhoneNumber,
} from '../config/enum';

const phoneNumberValidator = new RegExp(
  String.raw`^\d{${PhoneNumber.MIN_LENGTH},${PhoneNumber.MAX_LENGTH}}$`,
);
const emailValidator =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const passwordValidator =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?~-])[A-Za-z\d!@#$%^&*()_+[\]{};':"\\|,.<>/?~-]{12,}$/;
const timeSlotValidator =
  /^(0[1-9]|1[0-2]):([0-5][0-9]) [AP][M] - (0[1-9]|1[0-2]):([0-5][0-9]) [AP][M]$/;
const dateFormatValidator = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|1[0-9]|2[0-9]|3[0-1])$/;

/**
 * Regex that check only text and num
 * @param {any} minLength minLength
 * @param {any} maxLength maxLength
 * @returns {any} RegExp
 */
const textAndNumberRegex = (minLength: number, maxLength: number) => {
  const regexPattern = `^[A-Za-z0-9]{${minLength},${maxLength}}$`;
  return new RegExp(regexPattern, 'u');
};

/**
 * Function that check only text and num
 * @param {any} minLength minLength
 * @param {any} maxLength maxLength
 * @param {any} value value
 * @returns {boolean} boolean
 */
const textAndNumberValidation = (minLength: number, maxLength: number, value: string) => {
  const TEXT_NUMBER_REGEX = textAndNumberRegex(minLength, maxLength);
  return TEXT_NUMBER_REGEX.test(value);
};

/**
 * Email Validation
 * @param {any} value value
 * @returns {boolean} boolean
 */
const emailValidation = (value: string) => emailValidator.test(value);

/**
 * Phone number validation
 * @param {any} value value
 * @returns {boolean} boolean
 */
const phoneNumberValidation = (value: string) => phoneNumberValidator.test(value);

/**
 * Password Validation (Minimum 12 characters, should include one uppercase letter, one lowercase letter and one special character)
 * @param {any} value value
 * @returns {boolean} boolean
 */
const passwordValidation = (value: string) => passwordValidator.test(value);

/**
 * check text for length validation
 * @param {any} minLength minLength
 * @param {any} maxLength maxLength
 * @returns {any} RegExp
 */
const createStringRegex = (minLength: number, maxLength: number) => {
  const regexPattern = `^[\\p{L}\\d][\\p{L}\\p{N}._ @-]{${minLength - 1},${maxLength - 1}}$`;
  return new RegExp(regexPattern, 'u');
};

/**
 * Check the text and all symbols
 * @param {any} maxLength maxLength
 * @returns {any} RegExp
 */
const createTextAreaRegex = (maxLength: number = 500) => {
  const regexPattern = `^[\\p{L}\\p{N} .,!?;:'"()\\[\\]{}_/+-]{1,${maxLength}}$`;
  return new RegExp(regexPattern, 'u');
};

/**
 * Name validation use min and max length function
 * @param {any} minLength minLength
 * @param {any} maxLength maxLength
 * @param {any} name name
 * @returns {boolean} boolean
 */
const stringValidation = (minLength: number, maxLength: number, name: string = '') => {
  const NAME_REGEX = createStringRegex(minLength, maxLength);
  return NAME_REGEX.test(name);
};

/**
 * Check the description text validation
 * @param {any} maxLength maxLength
 * @param {any} description description
 * @returns {boolean} boolean
 */
const longTextValidation = (maxLength: number, description: string = '') => {
  const DESCRIPTION_REGEX = createTextAreaRegex(maxLength);
  return DESCRIPTION_REGEX.test(description);
};

/**
 * Address validation - allows letters, combining marks, numbers, punctuation and symbols of any
 * language (e.g. the Arabic comma "،" returned by Google for Saudi addresses)
 * @param {any} maxLength maxLength
 * @param {any} address address
 * @returns {boolean} boolean
 */
const addressValidation = (maxLength: number, address: string = '') => {
  const ADDRESS_REGEX = new RegExp(`^[\\p{L}\\p{M}\\p{N}\\p{P}\\p{S}\\p{Zs}]{1,${maxLength}}$`, 'u');
  return ADDRESS_REGEX.test(address?.trim() ?? '');
};

/**
 * Check whether the address was resolved from a Google suggestion - typing in the address input
 * clears latitude, longitude and geoLocations, so this is only true after a successful selection
 * @param {any} details - The station details holding latitude, longitude and geoLocations
 * @returns {boolean} boolean
 */
const isAddressResolved = (details: {
  latitude?: number | string | null;
  longitude?: number | string | null;
  geoLocations?: Array<{ key: string }> | null;
}) =>
  !!(
    details?.latitude &&
    details?.longitude &&
    details?.geoLocations?.some((location) => location.key !== 'country')
  );

/**
 * Only number validation function
 * @param {any} min min
 * @param {any} max max
 * @param {any} number number
 * @returns {boolean} boolean
 */
const integerValidation = (min: number, max: number, number: number = 0) => {
  const value = Number(number);
  const numberValidator = new RegExp(`^[0-9]\\d{${min - 1},${max - 1}}$`);
  return numberValidator.test(String(value));
};

/**
 * Check file size validation
 * @param {any} files files
 * @param {any} sizeMB sizeMB
 * @param {any} maxCount maxCount
 * @param {any} videoSize videoSize
 * @param {any} supportedMimeTypes supportedMimeTypes
 * @returns {boolean} boolean
 */
const checkFileSizeValidation = (
  files: File[],
  sizeMB: number,
  maxCount: number,
  videoSize: number = 100,
  supportedMimeTypes: string[] = [...imageMimeTypes, ...videoMimeTypes, ...documentMimeTypes],
) => {
  if (files.length > maxCount) {
    return false;
  }

  const MAX_FILE_SIZE = sizeMB * 1024 * 1024; // sizeMB in bytes
  const MAX_VIDEO_SIZE = videoSize * 1024 * 1024; // videoSize in bytes

  return files.every((file) => {
    // Check if file type is supported
    if (!supportedMimeTypes.includes(file.type)) {
      return false;
    }

    // Check file size based on type
    if (file.type === mimeTypes.MP4) {
      return file.size <= MAX_VIDEO_SIZE;
    }
    return file.size <= MAX_FILE_SIZE;
  });
};

/**
 * Check edit file size validation
 * @param {any} files files
 * @param {any} sizeMB sizeMB
 * @param {any} maxCount maxCount
 * @returns {boolean} boolean
 */
const checkEditFileSizeValidation = (files: File[], sizeMB: number, maxCount: number) => {
  let output = true;
  if (files.length > maxCount) {
    return false;
  }
  const MAX_FILE_SIZE = sizeMB * 1024 * 1024; // sizeMB MB
  files.forEach((file) => {
    if (file.size > MAX_FILE_SIZE) {
      output = false;
    }
  });
  return output;
};

/**
 * Handle the 12 hours validation
 * @param {any} time time
 * @returns {boolean} boolean
 */
const time12HoursValidation = (time: string) => {
  return /^(0[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/.test(time);
};

/**
 * Time slot validation
 * @param {any} value value
 * @returns {boolean} boolean
 */
const timeSlotValidation = (value: string) => timeSlotValidator.test(value);

/**
 * Date format validation
 * @param {any} value value
 * @returns {boolean} boolean
 */
const dateValidation = (value: string) => dateFormatValidator.test(value);
/**
 * Function to check floating number within the range or not
 * @param {any} min min
 * @param {any} max max
 * @param {any} number number
 * @returns {boolean} boolean
 */
const floatingNumberValidation = (min: number, max: number, number: number = 0) => {
  const value = Number(number);

  // Ensure value is a valid number and within range
  if (Number.isNaN(value) || value < min || value > max) return false;

  // Regex to match integer or decimal numbers (without restricting decimal places)
  const numberValidator = /^\d+(\.\d+)?$/;

  return numberValidator.test(String(value));
};
/**
 * Partner / Account Name validation (Allows A-Z, a-z, 0-9, space, hyphen -, apostrophe ', dot .)
 * Disallows special characters like @ # $ % ^ & * ( ) ! _ + = { } [ ] \ / | < > ? : ; " ,
 * @param {number} minLength - Minimum character length
 * @param {number} maxLength - Maximum character length
 * @param {string} name - The partner name string
 * @returns {boolean} True if valid, false otherwise
 */
const partnerNameValidation = (minLength: number = 1, maxLength: number = 100, name: string = '') => {
  if (!name || name.trim().length < minLength || name.length > maxLength) return false;
  const PARTNER_NAME_REGEX = /^[\p{L}0-9 .'-]+$/u;
  return PARTNER_NAME_REGEX.test(name.trim());
};

export {
  textAndNumberValidation,
  emailValidation,
  phoneNumberValidation,
  passwordValidation,
  stringValidation,
  longTextValidation,
  addressValidation,
  isAddressResolved,
  integerValidation,
  checkFileSizeValidation,
  time12HoursValidation,
  checkEditFileSizeValidation,
  timeSlotValidation,
  dateValidation,
  floatingNumberValidation,
  partnerNameValidation,
};
